import type { DocumentAdapter } from './adapter.js';
import {
  NotFoundError,
  QuotaExceededError,
  ReadOnlyError,
  RecipeCeilingError,
  SubscriptionLapsedError,
  SystemCollectionError,
  ValidationError,
} from './errors.js';
import { keyForAppend, keyForPosition, sortByOrder, type Ordered } from './order.js';
import type {
  CollectionRecord,
  CookDateRecord,
  IngredientRecord,
  InstructionRecord,
  NutritionFactRecord,
  PhotoRecord,
  RecipeRecord,
} from './records.js';
import { isRepairNeeded, mainCollectionRecord, planRepairs } from './repair.js';
import type { RecipeStore, RevisionBackend, StoreContext } from './store.js';
import type {
  Collection,
  CollectionId,
  CookDate,
  CookDateId,
  DayNote,
  DayNoteId,
  Entitlement,
  Ingredient,
  IngredientId,
  IngredientPatch,
  Instruction,
  InstructionId,
  InstructionPatch,
  NewCollection,
  NewIngredient,
  NewInstruction,
  NewNutritionFact,
  NewPhoto,
  NewRecipe,
  NutritionFact,
  NutritionFactId,
  NutritionFactPatch,
  Photo,
  PhotoId,
  Recipe,
  RecipeId,
  RecipePatch,
  RecipeQuery,
  RevisionSummary,
  TagDisplayCase,
  Unsubscribe,
} from './types.js';
import {
  CURRENT_SCHEMA_VERSION,
  DEFAULT_TAG_DISPLAY_CASE,
  FREE_ENTITLEMENT,
  MAIN_COLLECTION_ID,
  MAX_HOUSEHOLD_RECIPES,
  META_ENTITLEMENT_KEY,
  parseEntitlement,
} from './types.js';
import { scheduleMicrotask } from './microtask.js';
import {
  clampTimestamp,
  normalizeCookDate,
  normalizeCookDateScale,
  normalizeDayNote,
  normalizeMinutes,
  normalizeNutritionBasis,
  normalizeSourceType,
  normalizeTags,
  normalizeText,
  normalizeTitle,
} from './validation.js';

export interface RecipeStoreOptions {
  readonly adapter: DocumentAdapter;
  readonly context: StoreContext;
  readonly revisions?: RevisionBackend;
}

interface Subscription {
  query: RecipeQuery;
  cb: (rows: Recipe[]) => void;
}

/**
 * The whole domain, written once against DocumentAdapter. Both the Yjs-backed store and
 * the in-memory test store are this class with a different adapter, which means UI tests
 * exercise the same validation, ordering, and repair logic that production does.
 */
export class BaseRecipeStore implements RecipeStore {
  readonly context: StoreContext;
  protected readonly adapter: DocumentAdapter;
  private readonly revisions: RevisionBackend | undefined;
  private readonly subscriptions = new Set<Subscription>();
  private readonly allListeners = new Set<() => void>();
  private readonly unsubscribeAdapter: Unsubscribe;
  private reconcileScheduled = false;
  private destroyed = false;

  constructor(options: RecipeStoreOptions) {
    this.adapter = options.adapter;
    this.context = options.context;
    this.revisions = options.revisions;

    this.seed();
    this.unsubscribeAdapter = this.adapter.onChange(() => this.handleChange());
    this.reconcile();
  }

  // ---------------------------------------------------------------- lifecycle

  /**
   * Seeds meta and the Main collection. Every value written here is deterministic, so
   * two clients opening the same empty document offline converge instead of racing.
   */
  private seed(): void {
    this.adapter.transact(() => {
      if (this.adapter.getMeta('schemaVersion') == null) {
        this.adapter.setMeta('schemaVersion', CURRENT_SCHEMA_VERSION);
      }
      if (this.adapter.getMeta('householdId') == null) {
        this.adapter.setMeta('householdId', this.context.householdId);
      }
      if (this.adapter.getCollection(MAIN_COLLECTION_ID) == null) {
        this.adapter.createCollection(mainCollectionRecord());
      }
    }, 'seed');
  }

  private handleChange(): void {
    if (this.destroyed) return;
    this.notify();
    if (this.reconcileScheduled) return;
    this.reconcileScheduled = true;
    scheduleMicrotask(() => {
      this.reconcileScheduled = false;
      if (!this.destroyed) this.reconcile();
    });
  }

  private notify(): void {
    for (const listener of this.allListeners) listener();
    for (const sub of this.subscriptions) sub.cb(this.listRecipes(sub.query));
  }

  /**
   * Applies the repair plan locally so an offline device shows the corrected state right
   * away rather than waiting for the server's reconciler to catch up. Idempotent, so the
   * client and the Durable Object both running it is harmless.
   */
  reconcile(): void {
    if (this.destroyed || this.context.role === 'viewer') return;
    const plan = planRepairs(this.adapter.listRecipeMemberships(), this.adapter.listCollections());
    if (!isRepairNeeded(plan)) return;

    this.adapter.transact(() => {
      for (const { recipeId, collectionId } of plan.dropMemberships) {
        this.adapter.setRecipeCollection(recipeId, collectionId, null);
      }
      for (const recipeId of plan.adoptIntoMain) {
        if (this.adapter.getCollection(MAIN_COLLECTION_ID) == null) {
          this.adapter.createCollection(mainCollectionRecord());
        }
        const order = keyForAppend(this.membersOf(MAIN_COLLECTION_ID));
        this.adapter.setRecipeCollection(recipeId, MAIN_COLLECTION_ID, order);
      }
    }, 'reconcile');
  }

  destroy(): void {
    this.destroyed = true;
    this.unsubscribeAdapter();
    this.subscriptions.clear();
    this.allListeners.clear();
    this.adapter.destroy();
  }

  // ---------------------------------------------------------------- helpers

  private assertWritable(operation: string): void {
    if (this.context.role === 'viewer') throw new ReadOnlyError(operation);
    this.assertNotLapsed();
  }

  /**
   * The lock lives inside `assertWritable` rather than at ~30 call sites, which is the
   * whole reason that helper exists. A lapsed household is read-only in the strongest
   * sense: nothing local changes until payment recovers.
   */
  private assertNotLapsed(): void {
    if (this.getEntitlement().plan === 'locked') throw new SubscriptionLapsedError();
  }

  private assertWithinQuota(): void {
    const limit = this.getRecipeLimit();
    const count = this.adapter.countLiveRecipes();
    if (limit != null && count >= limit) throw new QuotaExceededError(limit, count);
    if (count >= MAX_HOUSEHOLD_RECIPES) throw new RecipeCeilingError(count);
  }

  private now(): number {
    return clampTimestamp(this.context.now(), this.context.lastKnownServerTime?.() ?? null);
  }

  private requireRecipe(id: RecipeId): RecipeRecord {
    const record = this.adapter.getRecipe(id);
    if (record == null) throw new NotFoundError('Recipe', id);
    return record;
  }

  private requireCollection(id: CollectionId): CollectionRecord {
    const record = this.adapter.getCollection(id);
    if (record == null) throw new NotFoundError('Collection', id);
    return record;
  }

  private touch(id: RecipeId): void {
    this.adapter.setRecipeField(id, 'updatedAt', this.now());
    this.adapter.setRecipeField(id, 'updatedBy', this.context.userId);
  }

  private touchCollection(id: CollectionId): void {
    this.adapter.setCollectionField(id, 'updatedAt', this.now());
  }

  /** Recipes in a collection, as `Ordered` values keyed on that collection's index. */
  private membersOf(collectionId: CollectionId, excludeRecipeId?: RecipeId): Ordered[] {
    const members = this.adapter
      .listCollectionMembers(collectionId)
      .filter((member) => member.id !== excludeRecipeId);
    return sortByOrder(members);
  }

  // ---------------------------------------------------------------- recipes

  async addRecipe(input: NewRecipe, into: readonly CollectionId[]): Promise<RecipeId> {
    this.assertWritable('add a recipe');
    this.assertWithinQuota();
    const id = input.id ?? this.context.newId();
    const now = this.now();

    // A recipe must land in at least one collection, so an empty request goes to Main
    // rather than being rejected. Unknown ids are dropped for the same reason: an import
    // referencing a collection that no longer exists should still produce a usable recipe.
    const known = new Set(this.adapter.listCollections().map((c) => c.id));
    const targets = [...new Set(into)].filter((c) => known.has(c));
    if (targets.length === 0) targets.push(MAIN_COLLECTION_ID);

    this.adapter.transact(() => {
      const record: RecipeRecord = {
        id,
        title: normalizeTitle(input.title),
        description: normalizeText(input.description),
        sourceType: normalizeSourceType(input.sourceType),
        sourceUrl: normalizeText(input.sourceUrl, 2048),
        sourceText: normalizeText(input.sourceText),
        servings: normalizeText(input.servings, 120),
        prepMinutes: normalizeMinutes(input.prepMinutes),
        cookMinutes: normalizeMinutes(input.cookMinutes),
        nutritionBasis: normalizeNutritionBasis(input.nutritionBasis),
        notes: normalizeText(input.notes),
        createdAt: now,
        createdBy: this.context.userId,
        updatedAt: now,
        updatedBy: this.context.userId,
        deletedAt: null,
        importedFrom: input.importedFrom ?? null,
        collections: {},
        tags: normalizeTags(input.tags ?? []),
        ingredients: [],
        instructions: [],
        photos: [],
        cookDates: [],
        nutritionFacts: [],
      };
      this.adapter.createRecipe(record);

      for (const collectionId of targets) {
        this.adapter.setRecipeCollection(id, collectionId, keyForAppend(this.membersOf(collectionId)));
      }

      let ingredientOrder: Ordered[] = [];
      for (const ingredient of input.ingredients ?? []) {
        const order = keyForAppend(ingredientOrder);
        const record2: IngredientRecord = {
          id: ingredient.id ?? this.context.newId(),
          order,
          groupHeading: normalizeText(ingredient.groupHeading, 200),
          quantity: normalizeText(ingredient.quantity, 60),
          unit: normalizeText(ingredient.unit, 60),
          name: normalizeText(ingredient.name, 300),
          note: normalizeText(ingredient.note, 500),
        };
        this.adapter.addIngredient(id, record2);
        ingredientOrder = [...ingredientOrder, { id: record2.id, order }];
      }

      let instructionOrder: Ordered[] = [];
      for (const instruction of input.instructions ?? []) {
        const order = keyForAppend(instructionOrder);
        const record2: InstructionRecord = {
          id: instruction.id ?? this.context.newId(),
          order,
          text: normalizeText(instruction.text),
        };
        this.adapter.addInstruction(id, record2);
        instructionOrder = [...instructionOrder, { id: record2.id, order }];
      }

      let photoOrder: Ordered[] = [];
      for (const photo of input.photos ?? []) {
        const order = keyForAppend(photoOrder);
        const record2: PhotoRecord = {
          id: photo.id ?? this.context.newId(),
          order,
          blobId: photo.blobId,
          width: photo.width,
          height: photo.height,
          blurhash: photo.blurhash ?? '',
        };
        this.adapter.addPhoto(id, record2);
        photoOrder = [...photoOrder, { id: record2.id, order }];
      }

      let nutritionOrder: Ordered[] = [];
      for (const fact of input.nutritionFacts ?? []) {
        const order = keyForAppend(nutritionOrder);
        const record2: NutritionFactRecord = {
          id: fact.id ?? this.context.newId(),
          order,
          label: normalizeText(fact.label, 200),
          amount: normalizeText(fact.amount, 60),
          unit: normalizeText(fact.unit, 60),
        };
        this.adapter.addNutritionFact(id, record2);
        nutritionOrder = [...nutritionOrder, { id: record2.id, order }];
      }
    }, 'addRecipe');

    return id;
  }

  async updateRecipe(id: RecipeId, patch: RecipePatch): Promise<void> {
    this.assertWritable('edit a recipe');
    this.requireRecipe(id);
    this.adapter.transact(() => {
      if (patch.title !== undefined) {
        this.adapter.setRecipeField(id, 'title', normalizeTitle(patch.title));
      }
      if (patch.description !== undefined) {
        this.adapter.setRecipeText(id, 'description', normalizeText(patch.description));
      }
      if (patch.notes !== undefined) {
        this.adapter.setRecipeText(id, 'notes', normalizeText(patch.notes));
      }
      if (patch.sourceType !== undefined) {
        this.adapter.setRecipeField(id, 'sourceType', normalizeSourceType(patch.sourceType));
      }
      if (patch.sourceUrl !== undefined) {
        this.adapter.setRecipeField(id, 'sourceUrl', normalizeText(patch.sourceUrl, 2048));
      }
      if (patch.sourceText !== undefined) {
        this.adapter.setRecipeField(id, 'sourceText', normalizeText(patch.sourceText));
      }
      if (patch.servings !== undefined) {
        this.adapter.setRecipeField(id, 'servings', normalizeText(patch.servings, 120));
      }
      if (patch.prepMinutes !== undefined) {
        this.adapter.setRecipeField(id, 'prepMinutes', normalizeMinutes(patch.prepMinutes));
      }
      if (patch.cookMinutes !== undefined) {
        this.adapter.setRecipeField(id, 'cookMinutes', normalizeMinutes(patch.cookMinutes));
      }
      if (patch.nutritionBasis !== undefined) {
        this.adapter.setRecipeField(id, 'nutritionBasis', normalizeNutritionBasis(patch.nutritionBasis));
      }
      this.touch(id);
    }, 'updateRecipe');
  }

  /**
   * Soft delete. The recipe's key is never removed from the document, because removing it
   * would destroy the nested type and silently discard a concurrent edit from another
   * device. `deletedAt` is an ordinary last-write-wins field, so delete and edit race
   * cleanly and the recipe stays recoverable from the trash.
   */
  async deleteRecipe(id: RecipeId): Promise<void> {
    this.assertWritable('delete a recipe');
    this.requireRecipe(id);
    this.adapter.transact(() => {
      this.adapter.setRecipeField(id, 'deletedAt', this.now());
      this.touch(id);
    }, 'deleteRecipe');
  }

  async restoreRecipe(id: RecipeId): Promise<void> {
    this.assertWritable('restore a recipe');
    const record = this.requireRecipe(id);
    // Only a genuine restore consumes a slot. The API allows "restoring" a recipe that is
    // already live and nothing in the UI does it, but it should stay a harmless no-op
    // rather than becoming the one operation a full household cannot perform.
    if (record.deletedAt != null) this.assertWithinQuota();
    this.adapter.transact(() => {
      this.adapter.setRecipeField(id, 'deletedAt', null);
      this.touch(id);
    }, 'restoreRecipe');
    this.reconcile();
  }

  // ---------------------------------------------------------------- ingredients

  async addIngredient(recipeId: RecipeId, input: NewIngredient, atIndex?: number): Promise<IngredientId> {
    this.assertWritable('add an ingredient');
    const recipe = this.requireRecipe(recipeId);
    const id = input.id ?? this.context.newId();
    const sorted = sortByOrder(recipe.ingredients);
    const order = atIndex == null ? keyForAppend(sorted) : keyForPosition(sorted, atIndex);

    this.adapter.transact(() => {
      this.adapter.addIngredient(recipeId, {
        id,
        order,
        groupHeading: normalizeText(input.groupHeading, 200),
        quantity: normalizeText(input.quantity, 60),
        unit: normalizeText(input.unit, 60),
        name: normalizeText(input.name, 300),
        note: normalizeText(input.note, 500),
      });
      this.touch(recipeId);
    }, 'addIngredient');
    return id;
  }

  async updateIngredient(
    recipeId: RecipeId,
    ingredientId: IngredientId,
    patch: IngredientPatch,
  ): Promise<void> {
    this.assertWritable('edit an ingredient');
    const recipe = this.requireRecipe(recipeId);
    if (!recipe.ingredients.some((i) => i.id === ingredientId)) {
      throw new NotFoundError('Ingredient', ingredientId);
    }
    this.adapter.transact(() => {
      if (patch.groupHeading !== undefined) {
        this.adapter.setIngredientField(recipeId, ingredientId, 'groupHeading', normalizeText(patch.groupHeading, 200));
      }
      if (patch.quantity !== undefined) {
        this.adapter.setIngredientField(recipeId, ingredientId, 'quantity', normalizeText(patch.quantity, 60));
      }
      if (patch.unit !== undefined) {
        this.adapter.setIngredientField(recipeId, ingredientId, 'unit', normalizeText(patch.unit, 60));
      }
      if (patch.name !== undefined) {
        this.adapter.setIngredientField(recipeId, ingredientId, 'name', normalizeText(patch.name, 300));
      }
      if (patch.note !== undefined) {
        this.adapter.setIngredientField(recipeId, ingredientId, 'note', normalizeText(patch.note, 500));
      }
      this.touch(recipeId);
    }, 'updateIngredient');
  }

  /**
   * A move writes one fractional index string. Yjs has no move operation, so the obvious
   * Y.Array implementation would be delete-then-insert — and two people moving the same
   * row concurrently would end up with the row appearing twice. Rewriting a single
   * last-write-wins field instead makes that duplication structurally impossible.
   */
  async moveIngredient(recipeId: RecipeId, ingredientId: IngredientId, toIndex: number): Promise<void> {
    this.assertWritable('reorder ingredients');
    const recipe = this.requireRecipe(recipeId);
    if (!recipe.ingredients.some((i) => i.id === ingredientId)) {
      throw new NotFoundError('Ingredient', ingredientId);
    }
    const others = sortByOrder(recipe.ingredients.filter((i) => i.id !== ingredientId));
    const order = keyForPosition(others, toIndex);
    this.adapter.transact(() => {
      this.adapter.setIngredientField(recipeId, ingredientId, 'order', order);
      this.touch(recipeId);
    }, 'moveIngredient');
  }

  async removeIngredient(recipeId: RecipeId, ingredientId: IngredientId): Promise<void> {
    this.assertWritable('remove an ingredient');
    this.requireRecipe(recipeId);
    this.adapter.transact(() => {
      this.adapter.removeIngredient(recipeId, ingredientId);
      this.touch(recipeId);
    }, 'removeIngredient');
  }

  // ---------------------------------------------------------------- instructions

  async addInstruction(
    recipeId: RecipeId,
    input: NewInstruction,
    atIndex?: number,
  ): Promise<InstructionId> {
    this.assertWritable('add a step');
    const recipe = this.requireRecipe(recipeId);
    const id = input.id ?? this.context.newId();
    const sorted = sortByOrder(recipe.instructions);
    const order = atIndex == null ? keyForAppend(sorted) : keyForPosition(sorted, atIndex);
    this.adapter.transact(() => {
      this.adapter.addInstruction(recipeId, { id, order, text: normalizeText(input.text) });
      this.touch(recipeId);
    }, 'addInstruction');
    return id;
  }

  async updateInstruction(
    recipeId: RecipeId,
    instructionId: InstructionId,
    patch: InstructionPatch,
  ): Promise<void> {
    this.assertWritable('edit a step');
    const recipe = this.requireRecipe(recipeId);
    if (!recipe.instructions.some((i) => i.id === instructionId)) {
      throw new NotFoundError('Instruction', instructionId);
    }
    if (patch.text === undefined) return;
    this.adapter.transact(() => {
      this.adapter.setInstructionText(recipeId, instructionId, normalizeText(patch.text));
      this.touch(recipeId);
    }, 'updateInstruction');
  }

  async moveInstruction(
    recipeId: RecipeId,
    instructionId: InstructionId,
    toIndex: number,
  ): Promise<void> {
    this.assertWritable('reorder steps');
    const recipe = this.requireRecipe(recipeId);
    if (!recipe.instructions.some((i) => i.id === instructionId)) {
      throw new NotFoundError('Instruction', instructionId);
    }
    const others = sortByOrder(recipe.instructions.filter((i) => i.id !== instructionId));
    const order = keyForPosition(others, toIndex);
    this.adapter.transact(() => {
      this.adapter.setInstructionOrder(recipeId, instructionId, order);
      this.touch(recipeId);
    }, 'moveInstruction');
  }

  async removeInstruction(recipeId: RecipeId, instructionId: InstructionId): Promise<void> {
    this.assertWritable('remove a step');
    this.requireRecipe(recipeId);
    this.adapter.transact(() => {
      this.adapter.removeInstruction(recipeId, instructionId);
      this.touch(recipeId);
    }, 'removeInstruction');
  }

  // ---------------------------------------------------------------- photos

  async addPhoto(recipeId: RecipeId, input: NewPhoto, atIndex?: number): Promise<PhotoId> {
    this.assertWritable('add a photo');
    const recipe = this.requireRecipe(recipeId);
    if (!/^[0-9a-f]{64}$/.test(input.blobId)) {
      throw new ValidationError('Photo blobId must be a lowercase SHA-256 hex digest');
    }

    // Content addressing means the same image added on two devices is the same blob. If
    // it is already attached, adding it again is a no-op rather than a duplicate row.
    const existing = recipe.photos.find((p) => p.blobId === input.blobId);
    if (existing) return existing.id;

    const id = input.id ?? this.context.newId();
    const sorted = sortByOrder(recipe.photos);
    const order = atIndex == null ? keyForAppend(sorted) : keyForPosition(sorted, atIndex);
    this.adapter.transact(() => {
      this.adapter.addPhoto(recipeId, {
        id,
        order,
        blobId: input.blobId,
        width: input.width,
        height: input.height,
        blurhash: input.blurhash ?? '',
      });
      this.touch(recipeId);
    }, 'addPhoto');
    return id;
  }

  async movePhoto(recipeId: RecipeId, photoId: PhotoId, toIndex: number): Promise<void> {
    this.assertWritable('reorder photos');
    const recipe = this.requireRecipe(recipeId);
    if (!recipe.photos.some((p) => p.id === photoId)) throw new NotFoundError('Photo', photoId);
    const others = sortByOrder(recipe.photos.filter((p) => p.id !== photoId));
    const order = keyForPosition(others, toIndex);
    this.adapter.transact(() => {
      this.adapter.setPhotoField(recipeId, photoId, 'order', order);
      this.touch(recipeId);
    }, 'movePhoto');
  }

  /** The primary photo is simply the first one, so this is a move to index 0. */
  async setPrimaryPhoto(recipeId: RecipeId, photoId: PhotoId): Promise<void> {
    await this.movePhoto(recipeId, photoId, 0);
  }

  async removePhoto(recipeId: RecipeId, photoId: PhotoId): Promise<void> {
    this.assertWritable('remove a photo');
    this.requireRecipe(recipeId);
    this.adapter.transact(() => {
      this.adapter.removePhoto(recipeId, photoId);
      this.touch(recipeId);
    }, 'removePhoto');
  }

  // ---------------------------------------------------------------- cook dates

  async addCookDate(recipeId: RecipeId, date: string, scale?: number): Promise<CookDateId> {
    this.assertWritable('log a cook date');
    this.requireRecipe(recipeId);
    const id = this.context.newId();
    const normalised = normalizeCookDate(date);
    const normalisedScale = normalizeCookDateScale(scale);
    this.adapter.transact(() => {
      this.adapter.addCookDate(recipeId, {
        id,
        date: normalised,
        ...(normalisedScale != null && { scale: normalisedScale }),
      });
      this.touch(recipeId);
    }, 'addCookDate');
    return id;
  }

  async removeCookDate(recipeId: RecipeId, cookDateId: CookDateId): Promise<void> {
    this.assertWritable('remove a cook date');
    this.requireRecipe(recipeId);
    this.adapter.transact(() => {
      this.adapter.removeCookDate(recipeId, cookDateId);
      this.touch(recipeId);
    }, 'removeCookDate');
  }

  // ---------------------------------------------------------------- day notes

  listDayNotes(): DayNote[] {
    return this.adapter
      .listDayNotes()
      .sort((a, b) => {
        if (a.date !== b.date) return a.date < b.date ? -1 : 1;
        if (a.createdAt !== b.createdAt) return a.createdAt - b.createdAt;
        return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
      })
      .map((record) => Object.freeze({ ...record }));
  }

  async addDayNote(date: string, text: string): Promise<DayNoteId> {
    this.assertWritable('add a day note');
    const day = normalizeCookDate(date);
    const note = normalizeDayNote(text);
    if (note === '') throw new ValidationError('A day note cannot be empty');
    const id = this.context.newId();
    this.adapter.transact(() => {
      this.adapter.addDayNote({ id, date: day, text: note, createdAt: this.now() });
    }, 'addDayNote');
    return id;
  }

  async removeDayNote(dayNoteId: DayNoteId): Promise<void> {
    this.assertWritable('remove a day note');
    this.adapter.transact(() => {
      this.adapter.removeDayNote(dayNoteId);
    }, 'removeDayNote');
  }

  // ---------------------------------------------------------------- nutrition

  async addNutritionFact(
    recipeId: RecipeId,
    input: NewNutritionFact,
    atIndex?: number,
  ): Promise<NutritionFactId> {
    this.assertWritable('add a nutrition fact');
    const recipe = this.requireRecipe(recipeId);
    const id = input.id ?? this.context.newId();
    const sorted = sortByOrder(recipe.nutritionFacts ?? []);
    const order = atIndex == null ? keyForAppend(sorted) : keyForPosition(sorted, atIndex);

    this.adapter.transact(() => {
      this.adapter.addNutritionFact(recipeId, {
        id,
        order,
        label: normalizeText(input.label, 200),
        amount: normalizeText(input.amount, 60),
        unit: normalizeText(input.unit, 60),
      });
      this.touch(recipeId);
    }, 'addNutritionFact');
    return id;
  }

  async updateNutritionFact(
    recipeId: RecipeId,
    factId: NutritionFactId,
    patch: NutritionFactPatch,
  ): Promise<void> {
    this.assertWritable('edit a nutrition fact');
    const recipe = this.requireRecipe(recipeId);
    if (!(recipe.nutritionFacts ?? []).some((f) => f.id === factId)) {
      throw new NotFoundError('Nutrition fact', factId);
    }
    this.adapter.transact(() => {
      if (patch.label !== undefined) {
        this.adapter.setNutritionFactField(recipeId, factId, 'label', normalizeText(patch.label, 200));
      }
      if (patch.amount !== undefined) {
        this.adapter.setNutritionFactField(recipeId, factId, 'amount', normalizeText(patch.amount, 60));
      }
      if (patch.unit !== undefined) {
        this.adapter.setNutritionFactField(recipeId, factId, 'unit', normalizeText(patch.unit, 60));
      }
      this.touch(recipeId);
    }, 'updateNutritionFact');
  }

  async moveNutritionFact(
    recipeId: RecipeId,
    factId: NutritionFactId,
    toIndex: number,
  ): Promise<void> {
    this.assertWritable('reorder nutrition facts');
    const recipe = this.requireRecipe(recipeId);
    const facts = recipe.nutritionFacts ?? [];
    if (!facts.some((f) => f.id === factId)) {
      throw new NotFoundError('Nutrition fact', factId);
    }
    const others = sortByOrder(facts.filter((f) => f.id !== factId));
    const order = keyForPosition(others, toIndex);
    this.adapter.transact(() => {
      this.adapter.setNutritionFactField(recipeId, factId, 'order', order);
      this.touch(recipeId);
    }, 'moveNutritionFact');
  }

  async removeNutritionFact(recipeId: RecipeId, factId: NutritionFactId): Promise<void> {
    this.assertWritable('remove a nutrition fact');
    this.requireRecipe(recipeId);
    this.adapter.transact(() => {
      this.adapter.removeNutritionFact(recipeId, factId);
      this.touch(recipeId);
    }, 'removeNutritionFact');
  }

  // ---------------------------------------------------------------- collections

  async createCollection(input: NewCollection, withRecipe?: RecipeId): Promise<CollectionId> {
    this.assertWritable('create a collection');
    if (withRecipe != null) this.requireRecipe(withRecipe);
    const id = input.id ?? this.context.newId();
    const name = this.assertCollectionNameAvailable(input.name);
    const order = keyForAppend(sortByOrder(this.adapter.listCollections()));
    const now = this.now();
    this.adapter.transact(() => {
      this.adapter.createCollection({
        id,
        name,
        description: normalizeText(input.description, 2000),
        icon: normalizeText(input.icon, 60),
        order,
        isSystem: false,
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
      });
      if (withRecipe != null) {
        this.adapter.setRecipeCollection(withRecipe, id, keyForAppend([]));
        this.touch(withRecipe);
      }
    }, 'createCollection');
    return id;
  }

  async renameCollection(id: CollectionId, name: string): Promise<void> {
    await this.updateCollection(id, { name });
  }

  /**
   * Rejects a name that (after trim/whitespace-collapse, case-insensitively) collides with
   * another live collection. `excludeId` lets a rename keep its own current name. Returns the
   * normalized name so callers store exactly what was checked. This is a best-effort local
   * guard: two offline devices can still coin the same name, which merges to two collections.
   */
  private assertCollectionNameAvailable(name: string, excludeId?: CollectionId): string {
    const normalized = normalizeTitle(name);
    const needle = normalized.toLowerCase();
    for (const record of this.adapter.listCollections()) {
      if (record.deletedAt != null || record.id === excludeId) continue;
      if (record.name.trim().replace(/\s+/g, ' ').toLowerCase() === needle) {
        throw new ValidationError(`A collection named “${normalized}” already exists`);
      }
    }
    return normalized;
  }

  async updateCollection(id: CollectionId, patch: Partial<NewCollection>): Promise<void> {
    this.assertWritable('edit a collection');
    this.requireCollection(id);
    this.adapter.transact(() => {
      if (patch.name !== undefined) {
        this.adapter.setCollectionField(id, 'name', this.assertCollectionNameAvailable(patch.name, id));
      }
      if (patch.description !== undefined) {
        this.adapter.setCollectionField(id, 'description', normalizeText(patch.description, 2000));
      }
      if (patch.icon !== undefined) {
        this.adapter.setCollectionField(id, 'icon', normalizeText(patch.icon, 60));
      }
      this.touchCollection(id);
    }, 'updateCollection');
  }

  /**
   * Soft delete, held for the restore window. Recipe memberships are deliberately left
   * untouched: during the window nothing moves, so restoring the collection is a clean
   * rewind. Only a purge — which the server performs after the window — drops memberships,
   * at which point the reconciler adopts any newly-orphaned recipes into Main.
   */
  async deleteCollection(id: CollectionId): Promise<void> {
    this.assertWritable('delete a collection');
    const record = this.requireCollection(id);
    if (record.isSystem) throw new SystemCollectionError('delete');
    this.adapter.transact(() => {
      this.adapter.setCollectionField(id, 'deletedAt', this.now());
      this.touchCollection(id);
    }, 'deleteCollection');
  }

  async restoreCollection(id: CollectionId): Promise<void> {
    this.assertWritable('restore a collection');
    this.requireCollection(id);
    this.adapter.transact(() => {
      this.adapter.setCollectionField(id, 'deletedAt', null);
      this.touchCollection(id);
    }, 'restoreCollection');
  }

  async addRecipeToCollection(recipeId: RecipeId, collectionId: CollectionId): Promise<void> {
    this.assertWritable('add a recipe to a collection');
    const recipe = this.requireRecipe(recipeId);
    this.requireCollection(collectionId);
    if (recipe.collections[collectionId] != null) return;
    const order = keyForAppend(this.membersOf(collectionId));
    this.adapter.transact(() => {
      this.adapter.setRecipeCollection(recipeId, collectionId, order);
      this.touch(recipeId);
    }, 'addRecipeToCollection');
  }

  /**
   * Removing the last collection locally would create a zero-collection recipe, so the
   * mutation moves it to Main in the same transaction. The reconciler still exists for
   * the case this cannot see: two devices each removing one of the last two memberships.
   */
  async removeRecipeFromCollection(recipeId: RecipeId, collectionId: CollectionId): Promise<void> {
    this.assertWritable('remove a recipe from a collection');
    const recipe = this.requireRecipe(recipeId);
    if (recipe.collections[collectionId] == null) return;

    const remaining = Object.keys(recipe.collections).filter((c) => c !== collectionId);
    this.adapter.transact(() => {
      this.adapter.setRecipeCollection(recipeId, collectionId, null);
      if (remaining.length === 0 && collectionId !== MAIN_COLLECTION_ID) {
        const order = keyForAppend(this.membersOf(MAIN_COLLECTION_ID, recipeId));
        this.adapter.setRecipeCollection(recipeId, MAIN_COLLECTION_ID, order);
      }
      this.touch(recipeId);
    }, 'removeRecipeFromCollection');
  }

  async reorderRecipeInCollection(
    recipeId: RecipeId,
    collectionId: CollectionId,
    toIndex: number,
  ): Promise<void> {
    this.assertWritable('reorder recipes');
    const recipe = this.requireRecipe(recipeId);
    if (recipe.collections[collectionId] == null) {
      throw new NotFoundError('Recipe in collection', `${recipeId}/${collectionId}`);
    }
    const order = keyForPosition(this.membersOf(collectionId, recipeId), toIndex);
    this.adapter.transact(() => {
      this.adapter.setRecipeCollection(recipeId, collectionId, order);
      this.touch(recipeId);
    }, 'reorderRecipeInCollection');
  }

  // ---------------------------------------------------------------- tags

  async setTags(recipeId: RecipeId, tags: readonly string[]): Promise<void> {
    this.assertWritable('edit tags');
    const recipe = this.requireRecipe(recipeId);
    const next = normalizeTags(tags);
    const nextSet = new Set(next);
    const current = new Set(recipe.tags);

    this.adapter.transact(() => {
      for (const tag of current) if (!nextSet.has(tag)) this.adapter.setRecipeTag(recipeId, tag, false);
      for (const tag of next) if (!current.has(tag)) this.adapter.setRecipeTag(recipeId, tag, true);
      this.touch(recipeId);
    }, 'setTags');
  }

  async renameTagEverywhere(from: string, to: string): Promise<number> {
    this.assertWritable('rename a tag');
    const target = normalizeTags([to])[0];
    if (target == null) throw new ValidationError('Replacement tag cannot be empty');
    let changed = 0;
    this.adapter.transact(() => {
      for (const recipe of this.adapter.listRecipes()) {
        if (!recipe.tags.includes(from)) continue;
        this.adapter.setRecipeTag(recipe.id, from, false);
        if (!recipe.tags.includes(target)) this.adapter.setRecipeTag(recipe.id, target, true);
        this.touch(recipe.id);
        changed += 1;
      }
    }, 'renameTagEverywhere');
    return changed;
  }

  listTags(): { tag: string; count: number }[] {
    const counts = new Map<string, number>();
    for (const recipe of this.adapter.listRecipes()) {
      if (recipe.deletedAt != null) continue;
      for (const tag of recipe.tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => (b.count - a.count) || a.tag.localeCompare(b.tag));
  }

  // ---------------------------------------------------------------- entitlement

  /**
   * The household's plan, as the server last wrote it into the document.
   *
   * This is the offline copy, and the only channel a store with no network can see. A
   * tampering client can raise its own limit here, so the server never reads this value:
   * the Durable Object keeps its own cached copy and heals this key when they disagree.
   */
  getEntitlement(): Entitlement {
    return parseEntitlement(this.adapter.getMeta(META_ENTITLEMENT_KEY)) ?? FREE_ENTITLEMENT;
  }

  /** null means unlimited. */
  getRecipeLimit(): number | null {
    const entitlement = this.getEntitlement();
    return entitlement.plan === 'locked' ? 0 : entitlement.recipeLimit;
  }

  /** Live (non-trashed) recipes. Cheap: one field per recipe, nothing materialised. */
  liveRecipeCount(): number {
    return this.adapter.countLiveRecipes();
  }

  canAddRecipe(): boolean {
    const remaining = this.remainingRecipeSlots();
    if (remaining != null && remaining <= 0) return false;
    return this.adapter.countLiveRecipes() < MAX_HOUSEHOLD_RECIPES;
  }

  /** null means unlimited. Never negative: an overrun household reads as 0 remaining. */
  remainingRecipeSlots(): number | null {
    const limit = this.getRecipeLimit();
    if (limit == null) return null;
    return Math.max(0, limit - this.adapter.countLiveRecipes());
  }

  getTagDisplayCase(): TagDisplayCase {
    const value = this.adapter.getMeta<string>('tagDisplayCase');
    if (value === 'preserve' || value === 'lower' || value === 'title') return value;
    return DEFAULT_TAG_DISPLAY_CASE;
  }

  async setTagDisplayCase(mode: TagDisplayCase): Promise<void> {
    this.assertWritable('change tag display');
    this.adapter.transact(() => {
      this.adapter.setMeta('tagDisplayCase', mode);
    }, 'setTagDisplayCase');
  }

  getImportCollectionId(): CollectionId {
    const value = this.adapter.getMeta<string>('importCollectionId');
    // Fall back to Main when unset, or when the configured collection has since been
    // deleted, so imports always land somewhere real.
    if (typeof value === 'string') {
      const collection = this.adapter.getCollection(value);
      if (collection != null && collection.deletedAt == null) return value;
    }
    return MAIN_COLLECTION_ID;
  }

  async setImportCollectionId(id: CollectionId): Promise<void> {
    this.assertWritable('change import collection');
    this.adapter.transact(() => {
      this.adapter.setMeta('importCollectionId', id);
    }, 'setImportCollectionId');
  }

  isFavourite(recipeId: RecipeId): boolean {
    return this.adapter.isFavourite(this.context.userId, recipeId);
  }

  getFavouritedAt(recipeId: RecipeId): number | null {
    return this.adapter.getFavouritedAt(this.context.userId, recipeId);
  }

  async setFavourite(recipeId: RecipeId, favourite: boolean): Promise<void> {
    this.requireRecipe(recipeId);
    this.adapter.transact(() => {
      this.adapter.setFavouritedAt(
        this.context.userId,
        recipeId,
        favourite ? this.now() : null,
      );
    }, 'setFavourite');
  }

  listFavouriteIds(): ReadonlySet<RecipeId> {
    return new Set(this.adapter.listFavouriteIds(this.context.userId));
  }

  // ---------------------------------------------------------------- reads

  getRecipe(id: RecipeId): Recipe | null {
    const record = this.adapter.getRecipe(id);
    return record == null ? null : toRecipe(record);
  }

  listRecipes(query: RecipeQuery = {}): Recipe[] {
    const wantTags = query.tags?.length ? new Set(query.tags.map((t) => t.toLowerCase())) : null;
    const needle = query.search?.trim().toLowerCase();

    let rows = this.adapter.listRecipes().filter((record) => {
      const deleted = record.deletedAt != null;
      if (query.onlyDeleted === true) {
        if (!deleted) return false;
      } else if (deleted && query.includeDeleted !== true) {
        return false;
      }
      if (query.collectionId != null && record.collections[query.collectionId] == null) return false;
      if (wantTags != null && !record.tags.some((t) => wantTags.has(t.toLowerCase()))) return false;
      if (query.onlyFavourites === true && !this.adapter.isFavourite(this.context.userId, record.id)) {
        return false;
      }
      if (needle) {
        const haystack = [record.title, record.description, record.notes, ...record.tags]
          .join('\n')
          .toLowerCase();
        const inIngredients = record.ingredients.some((i) => i.name.toLowerCase().includes(needle));
        if (!haystack.includes(needle) && !inIngredients) return false;
      }
      return true;
    });

    const sort =
      query.sort ??
      (query.onlyFavourites === true
        ? 'favouritedAt'
        : query.collectionId != null
          ? 'collectionOrder'
          : 'title');
    const collectionId = query.collectionId;
    const userId = this.context.userId;
    rows = rows.sort((a, b) => {
      switch (sort) {
        case 'updatedAt':
          return b.updatedAt - a.updatedAt || a.id.localeCompare(b.id);
        case 'createdAt':
          return b.createdAt - a.createdAt || a.id.localeCompare(b.id);
        case 'favouritedAt': {
          const at = (id: RecipeId) => this.adapter.getFavouritedAt(userId, id) ?? 0;
          return at(b.id) - at(a.id) || a.id.localeCompare(b.id);
        }
        case 'collectionOrder': {
          if (collectionId == null) break;
          const ao = a.collections[collectionId] ?? '';
          const bo = b.collections[collectionId] ?? '';
          if (ao !== bo) return ao < bo ? -1 : 1;
          return a.id.localeCompare(b.id);
        }
        default:
          break;
      }
      return a.title.localeCompare(b.title) || a.id.localeCompare(b.id);
    });

    if (query.limit != null) rows = rows.slice(0, query.limit);
    return rows.map(toRecipe);
  }

  listCollections(includeDeleted = false): Collection[] {
    return sortByOrder(this.adapter.listCollections())
      .filter((c) => includeDeleted || c.deletedAt == null)
      .map(toCollection);
  }

  getCollection(id: CollectionId): Collection | null {
    const record = this.adapter.getCollection(id);
    return record == null ? null : toCollection(record);
  }

  subscribe(query: RecipeQuery, cb: (rows: Recipe[]) => void): Unsubscribe {
    const sub: Subscription = { query, cb };
    this.subscriptions.add(sub);
    cb(this.listRecipes(query));
    return () => this.subscriptions.delete(sub);
  }

  subscribeAll(cb: () => void): Unsubscribe {
    this.allListeners.add(cb);
    return () => this.allListeners.delete(cb);
  }

  // ---------------------------------------------------------------- revisions

  async listRevisions(recipeId: RecipeId): Promise<RevisionSummary[]> {
    if (this.revisions == null) return [];
    return this.revisions.list(recipeId);
  }

  async getRevision(recipeId: RecipeId, revisionNo: number): Promise<Recipe> {
    if (this.revisions == null) throw new NotFoundError('Revision', `${recipeId}@${revisionNo}`);
    return this.revisions.get(recipeId, revisionNo);
  }

  /**
   * Restore is a forward edit, never a document rollback. Rolling the CRDT back would
   * fight with any concurrent replica and undo their work on the next merge; replaying the
   * old content through ordinary mutations converges normally and leaves the restore
   * itself in the history as an auditable change.
   */
  async restoreRevision(recipeId: RecipeId, revisionNo: number): Promise<void> {
    this.assertWritable('restore a revision');
    const snapshot = await this.getRevision(recipeId, revisionNo);
    await this.applySnapshot(recipeId, snapshot);
  }

  /** Also used by share import to write a manifest into an existing recipe. */
  async applySnapshot(recipeId: RecipeId, snapshot: Recipe): Promise<void> {
    this.assertWritable('restore a recipe');
    const current = this.requireRecipe(recipeId);

    this.adapter.transact(() => {
      this.adapter.setRecipeField(recipeId, 'title', normalizeTitle(snapshot.title));
      this.adapter.setRecipeText(recipeId, 'description', normalizeText(snapshot.description));
      this.adapter.setRecipeText(recipeId, 'notes', normalizeText(snapshot.notes));
      this.adapter.setRecipeField(recipeId, 'sourceType', normalizeSourceType(snapshot.sourceType));
      this.adapter.setRecipeField(recipeId, 'sourceUrl', normalizeText(snapshot.sourceUrl, 2048));
      this.adapter.setRecipeField(recipeId, 'sourceText', normalizeText(snapshot.sourceText));
      this.adapter.setRecipeField(recipeId, 'servings', normalizeText(snapshot.servings, 120));
      this.adapter.setRecipeField(recipeId, 'prepMinutes', normalizeMinutes(snapshot.prepMinutes));
      this.adapter.setRecipeField(recipeId, 'cookMinutes', normalizeMinutes(snapshot.cookMinutes));
      this.adapter.setRecipeField(
        recipeId,
        'nutritionBasis',
        normalizeNutritionBasis(snapshot.nutritionBasis),
      );

      syncChildren(
        current.ingredients,
        snapshot.ingredients,
        (item) => this.adapter.addIngredient(recipeId, { ...item }),
        (id) => this.adapter.removeIngredient(recipeId, id),
        (id, field, value) =>
          this.adapter.setIngredientField(recipeId, id, field as never, value),
      );
      syncChildren(
        current.instructions,
        snapshot.instructions,
        (item) => this.adapter.addInstruction(recipeId, { ...item }),
        (id) => this.adapter.removeInstruction(recipeId, id),
        (id, field, value) => {
          if (field === 'text') this.adapter.setInstructionText(recipeId, id, String(value));
          else this.adapter.setInstructionOrder(recipeId, id, String(value));
        },
      );
      syncChildren(
        current.photos,
        snapshot.photos,
        (item) => this.adapter.addPhoto(recipeId, { ...item }),
        (id) => this.adapter.removePhoto(recipeId, id),
        (id, field, value) => this.adapter.setPhotoField(recipeId, id, field as never, value),
      );

      syncChildren(
        current.cookDates,
        snapshot.cookDates ?? [],
        (item) => this.adapter.addCookDate(recipeId, { ...item }),
        (id) => this.adapter.removeCookDate(recipeId, id),
        (id, field, value) => {
          // Cook dates are immutable aside from add/remove; date changes replace the entry.
          if (field === 'date') {
            this.adapter.removeCookDate(recipeId, id);
            const scale = snapshot.cookDates?.find((item) => item.id === id)?.scale;
            this.adapter.addCookDate(recipeId, {
              id,
              date: String(value),
              ...(scale != null && { scale }),
            });
          }
        },
      );

      syncChildren(
        current.nutritionFacts ?? [],
        snapshot.nutritionFacts ?? [],
        (item) => this.adapter.addNutritionFact(recipeId, { ...item }),
        (id) => this.adapter.removeNutritionFact(recipeId, id),
        (id, field, value) => this.adapter.setNutritionFactField(recipeId, id, field as never, value),
      );

      const nextTags = new Set(normalizeTags(snapshot.tags));
      for (const tag of current.tags) if (!nextTags.has(tag)) this.adapter.setRecipeTag(recipeId, tag, false);
      for (const tag of nextTags) if (!current.tags.includes(tag)) this.adapter.setRecipeTag(recipeId, tag, true);

      this.touch(recipeId);
    }, 'applySnapshot');
  }
}

// -------------------------------------------------------------------- converters

function syncChildren<T extends { id: string }>(
  current: readonly T[],
  desired: readonly T[],
  add: (item: T) => void,
  remove: (id: string) => void,
  setField: (id: string, field: string, value: unknown) => void,
): void {
  const currentById = new Map(current.map((item) => [item.id, item]));
  const desiredById = new Map(desired.map((item) => [item.id, item]));

  for (const item of current) if (!desiredById.has(item.id)) remove(item.id);
  for (const item of desired) {
    const existing = currentById.get(item.id);
    if (existing == null) {
      add(item);
      continue;
    }
    for (const [key, value] of Object.entries(item)) {
      if (key === 'id') continue;
      if ((existing as Record<string, unknown>)[key] !== value) setField(item.id, key, value);
    }
  }
}

function toIngredient(record: IngredientRecord): Ingredient {
  return Object.freeze({ ...record });
}

function toInstruction(record: InstructionRecord): Instruction {
  return Object.freeze({ ...record });
}

function toPhoto(record: PhotoRecord): Photo {
  return Object.freeze({ ...record });
}

function toCookDate(record: CookDateRecord): CookDate {
  return Object.freeze({ ...record });
}

function toNutritionFact(record: NutritionFactRecord): NutritionFact {
  return Object.freeze({ ...record });
}

/**
 * Reads hand back deeply frozen plain objects. Nothing that reaches a component is a live
 * CRDT proxy, so a component cannot accidentally mutate the document by assigning to a
 * field, and React's identity-based memoization behaves predictably.
 */
export function toRecipe(record: RecipeRecord): Recipe {
  const collectionIds = Object.keys(record.collections).sort();
  return Object.freeze({
    id: record.id,
    title: record.title,
    description: record.description,
    sourceType: record.sourceType,
    sourceUrl: record.sourceUrl,
    sourceText: record.sourceText,
    servings: record.servings,
    prepMinutes: record.prepMinutes,
    cookMinutes: record.cookMinutes,
    nutritionBasis: record.nutritionBasis ?? 'unknown',
    nutritionFacts: Object.freeze(sortByOrder(record.nutritionFacts ?? []).map(toNutritionFact)),
    ingredients: Object.freeze(sortByOrder(record.ingredients).map(toIngredient)),
    instructions: Object.freeze(sortByOrder(record.instructions).map(toInstruction)),
    photos: Object.freeze(sortByOrder(record.photos).map(toPhoto)),
    cookDates: Object.freeze(
      [...(record.cookDates ?? [])]
        .sort((a, b) => (a.date === b.date ? a.id.localeCompare(b.id) : a.date.localeCompare(b.date)))
        .map(toCookDate),
    ),
    collectionIds: Object.freeze(collectionIds),
    collectionOrder: Object.freeze({ ...record.collections }),
    tags: Object.freeze([...record.tags].sort((a, b) => a.localeCompare(b))),
    notes: record.notes,
    createdAt: record.createdAt,
    createdBy: record.createdBy,
    updatedAt: record.updatedAt,
    updatedBy: record.updatedBy,
    deletedAt: record.deletedAt,
    importedFrom: record.importedFrom == null ? null : Object.freeze({ ...record.importedFrom }),
  });
}

export function toCollection(record: CollectionRecord): Collection {
  return Object.freeze({ ...record });
}
